import {StyledContainerTabs, StyledMeasuresContainer, StyledMeasuresWrapper} from '@pages/measures/style.ts'
import {Tab} from '@/features/measures/components/tab/Tab.tsx'
import {routes} from '@utilities/constants/routes.tsx'
import {useTranslation} from 'react-i18next'
import {useEffect, useMemo} from 'react'
import useWindowDimensions from '@hooks/useWindowDimensions.ts'
import {MeasureTableMobile} from '@/features/measures/components/table/measure-table-mobile/MeasureTableMobile.tsx'
import {MeasureTableDesktop} from '@/features/measures/components/table/measure-table-desktop/MeasureTableDesktop.tsx'
import {MeasureFilters} from '@components/ui/measure-filters/MeasureFilters.tsx'
import {useGetMeasures} from '@/features/measures/services/useGetMeasures.ts'
import {Filters, Measure} from '@/features/measures/services/types.ts'
import {useFilterMeasures} from '@hooks/useFilterMeasures.tsx'
import {useSearchParams} from '@hooks/useSearchParams.ts'
import {QuerySearchParams} from '@/features/measures/services/types.ts'

export const Measures = () => {
    const {t} = useTranslation()
    const {width} = useWindowDimensions()
    const isMobile = useMemo(() => width < 768, [width])
    const {filters, setFilters} = useFilterMeasures()
    const [searchParams] = useSearchParams(QuerySearchParams)
    const search = searchParams.query || ''

    const defaultSort = {
        orderBy: 'publishedAt' as const,
        orderDirection: 'desc' as const
    }

    const {remappedData, meta, isLoading, sort, setSort} = useGetMeasures({
        ...filters,
        search,
        ...defaultSort
    } as Filters)
    const measures = remappedData as Measure[]

    const handlePageChange = (page: number) => {
        setFilters({
            ...filters,
            page
        })
    }

    const handlePerPageChange = (newPerPage: number) => {
        setFilters({
            ...filters,
            perPage: newPerPage,
            page: 1
        })
    }

    useEffect(() => {
        setFilters({
            ...filters,
            page: filters.page || 1,
            perPage: filters.perPage || 50
        })
    }, [])

    return (
        <StyledMeasuresContainer>
            <StyledMeasuresWrapper>
                <StyledContainerTabs direction={'column'} gap={isMobile ? 5 : 9} fullWidth>
                    <Tab
                        to={routes.MEASURES.path}
                        label={t('measures:measures')}
                        counter={meta?.total?.toString()}
                        isLoading={isLoading}
                    />

                    <MeasureFilters />
                </StyledContainerTabs>
                {isMobile ? (
                    <MeasureTableMobile
                        measures={measures}
                        isLoading={isLoading}
                        meta={meta}
                        setCurrentPage={handlePageChange}
                        perPage={filters.perPage || 50}
                        setPerPage={handlePerPageChange}
                    />
                ) : (
                    <MeasureTableDesktop
                        measures={measures}
                        isLoading={isLoading}
                        meta={meta}
                        setCurrentPage={handlePageChange}
                        perPage={filters.perPage || 50}
                        setPerPage={handlePerPageChange}
                        sorter={{
                            orderBy: sort?.orderBy || 'publishedAt',
                            orderDirection: sort?.orderDirection || 'desc',
                            onSort: (orderBy, orderDirection) => {
                                setFilters({
                                    ...filters,
                                    page: 1
                                })
                                setSort({
                                    orderBy: orderBy || 'publishedAt',
                                    orderDirection
                                })
                            }
                        }}
                    />
                )}
            </StyledMeasuresWrapper>
        </StyledMeasuresContainer>
    )
}
